import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { uploadFileToVps, deleteFileFromVps } from "@/lib/vpsStorage";
import Swal from "sweetalert2";

interface Props {
  tenantId: string;
  tenantName: string;
  publicUrl: string | null;
}

const INPUT = "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:ring-2 focus:ring-[#C8A96E]/10 transition-all";

export default function SEOTab({ tenantId, tenantName, publicUrl }: Props) {
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [seoKeywords, setSeoKeywords] = useState("");
  const [ogImage, setOgImage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingOgFileId, setPendingOgFileId] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    const { data } = await supabase.from("tenant_website_content").select("seo_title, seo_description, seo_keywords, og_image").eq("tenant_id", tenantId).maybeSingle();
    if (data) {
      setExists(true);
      setSeoTitle(data.seo_title ?? "");
      setSeoDescription(data.seo_description ?? "");
      setSeoKeywords(data.seo_keywords ?? "");
      setOgImage(data.og_image ?? null);
    }
    setLoading(false);
  }, [tenantId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    setSaving(true);
    const payload = { seo_title: seoTitle, seo_description: seoDescription, seo_keywords: seoKeywords, og_image: ogImage, updated_at: new Date().toISOString() };
    let err: { message: string } | null = null;
    try {
      if (exists) {
        const res = await supabase.from("tenant_website_content").update(payload).eq("tenant_id", tenantId);
        err = res.error;
      } else {
        const res = await supabase.from("tenant_website_content").insert({ ...payload, tenant_id: tenantId, is_published: true });
        err = res.error;
        if (!err) setExists(true);
      }
      if (err) throw err;
      setSaved(true);
      setPendingOgFileId(null);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      const msg = e?.message || "Failed to save SEO settings";
      Swal.fire({ title: "Error", text: msg, icon: "error", confirmButtonColor: "#C8A96E", background: "#ffffff", color: "#0f172a" });
      if (pendingOgFileId) {
        await deleteFileFromVps({ fileId: pendingOgFileId, tenantId, silent: true });
        setPendingOgFileId(null);
      }
    }
    setSaving(false);
  };

  const handleRemoveOgImage = () => {
    setOgImage(null);
  };

  const handleOgImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { setUploadError("Only images allowed"); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadError("Max 10MB"); return; }
    setUploading(true);
    setUploadError(null);
    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        folder: "seo",
        allowedTypes: ["image/jpeg", "image/png", "image/webp", "image/gif"],
        maxSizeMB: 10,
      });
      if (!result.success || !result.url) {
        setUploadError(result.error || "Upload failed");
        Swal.fire({ title: "Error", text: result.error || "Upload failed", icon: "error", confirmButtonColor: "#C8A96E", background: "#ffffff", color: "#0f172a" });
      } else {
        setOgImage(result.url);
        if (result.fileId) setPendingOgFileId(result.fileId);
      }
    } catch {
      setUploadError("Upload failed. Try again.");
      Swal.fire({ title: "Error", text: "Upload failed. Try again.", icon: "error", confirmButtonColor: "#C8A96E", background: "#ffffff", color: "#0f172a" });
    }
    setUploading(false);
  };

  const titleLen = seoTitle.length;
  const descLen = seoDescription.length;
  const fullUrl = publicUrl ? `${window.location.origin}${publicUrl}` : `${window.location.origin}/s/your-studio`;

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-[#C8A96E]/8 border border-[#C8A96E]/20 rounded-xl p-4">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/15 flex-shrink-0">
          <i className="ri-search-eye-line text-[#9a7840] text-sm"></i>
        </div>
        <div>
          <p className="text-[#7a5e2a] font-semibold text-sm mb-0.5">Search Engine Optimization</p>
          <p className="text-[#9a7840] text-xs leading-relaxed">These settings control how your studio page appears in Google and other search engines. Good SEO helps potential clients find you.</p>
        </div>
      </div>

      {/* SEO Title */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50">
            <i className="ri-heading text-amber-500 text-sm"></i>
          </div>
          <h3 className="text-slate-700 font-semibold text-sm">Page Title</h3>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider">SEO Title</label>
            <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${titleLen > 55 ? "bg-amber-100 text-amber-600" : titleLen > 0 ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
              {titleLen}/60
            </span>
          </div>
          <input type="text" value={seoTitle} onChange={(e) => setSeoTitle(e.target.value)} maxLength={60} placeholder={`${tenantName} | Wedding Photography & Video`} className={INPUT} />
          <p className="text-slate-400 text-[11px] mt-1.5">Recommended: 50–60 characters. Shown in browser tab and Google results.</p>
        </div>
      </div>

      {/* SEO Description */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50">
            <i className="ri-file-text-line text-emerald-500 text-sm"></i>
          </div>
          <h3 className="text-slate-700 font-semibold text-sm">Meta Description</h3>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-slate-500 text-xs font-medium uppercase tracking-wider">Description</label>
            <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${descLen > 150 ? "bg-amber-100 text-amber-600" : descLen > 0 ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
              {descLen}/160
            </span>
          </div>
          <textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} rows={3} maxLength={160} placeholder="Professional wedding photography studio. We capture timeless moments with artistic vision and passion." className={INPUT + " resize-none"} />
          <p className="text-slate-400 text-[11px] mt-1.5">Recommended: 120–160 characters. Shown under your URL in Google results.</p>
        </div>
      </div>

      {/* Keywords */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-violet-50">
            <i className="ri-price-tag-3-line text-violet-500 text-sm"></i>
          </div>
          <h3 className="text-slate-700 font-semibold text-sm">Keywords</h3>
        </div>
        <div>
          <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5 block">Keywords <span className="text-slate-300 normal-case font-normal">(optional)</span></label>
          <input type="text" value={seoKeywords} onChange={(e) => setSeoKeywords(e.target.value)} maxLength={200} placeholder="wedding photographer, portrait session, engagement photos" className={INPUT} />
          <p className="text-slate-400 text-[11px] mt-1.5">Comma-separated. 3–5 keywords related to your services and location.</p>
        </div>
      </div>

      {/* OG Image */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50">
            <i className="ri-image-add-line text-blue-500 text-sm"></i>
          </div>
          <h3 className="text-slate-700 font-semibold text-sm">Open Graph Image</h3>
        </div>
        <div>
          <p className="text-slate-400 text-[11px] mb-3">Shown when your page is shared on Facebook, WhatsApp, LinkedIn, and other platforms. Recommended: 1200 x 630 px.</p>
          {ogImage ? (
            <div className="relative rounded-xl overflow-hidden border border-slate-200 group">
              <img src={ogImage} alt="OG preview" className="w-full h-48 object-cover object-top" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <button
                  onClick={handleRemoveOgImage}
                  className="flex items-center gap-1.5 bg-white text-red-500 text-xs font-medium px-3 py-2 rounded-lg shadow-sm hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <label className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-6 py-8 transition-colors cursor-pointer ${uploading ? "bg-slate-50 opacity-60" : "hover:border-[#C8A96E]/40 hover:bg-[#C8A96E]/5"}`}>
              <input type="file" accept="image/*" className="hidden" onChange={handleOgImageUpload} disabled={uploading} />
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                <i className="ri-upload-cloud-2-line text-lg"></i>
              </div>
              <p className="text-slate-500 text-xs font-medium">{uploading ? "Uploading..." : "Upload OG image"}</p>
              <p className="text-slate-300 text-[10px]">PNG or JPG, max 10MB</p>
            </label>
          )}
          {uploadError && <p className="text-red-500 text-xs mt-2">{uploadError}</p>}
        </div>
      </div>

      {/* Google Preview */}
      {(titleLen > 0 || descLen > 0 || ogImage) && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-google-line text-slate-400 text-sm"></i>
            </div>
            <p className="text-slate-500 text-xs font-medium uppercase tracking-wider">Google Preview</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
            <p className="text-slate-400 text-[11px] mb-0.5 font-mono truncate">{fullUrl}</p>
            <p className="text-blue-600 text-base font-medium leading-snug line-clamp-1 hover:underline cursor-pointer">
              {seoTitle || `${tenantName} | Photography & Video Services`}
            </p>
            <p className="text-slate-500 text-sm leading-relaxed mt-1 line-clamp-2">
              {seoDescription || "Professional photography studio."}
            </p>
          </div>
          <p className="text-slate-400 text-[11px] mt-2">This is an approximation of how your page may appear in Google search results.</p>
        </div>
      )}

      {/* SEO Score */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-slate-600 text-xs font-medium uppercase tracking-wider mb-3">SEO Checklist</h3>
        <div className="space-y-2">
          {[
            { label: "Title set", ok: titleLen > 0 },
            { label: "Title length optimal (50–60 chars)", ok: titleLen >= 50 && titleLen <= 60 },
            { label: "Description set", ok: descLen > 0 },
            { label: "Description length optimal (120–160 chars)", ok: descLen >= 120 && descLen <= 160 },
            { label: "Keywords added", ok: seoKeywords.trim().length > 0 },
            { label: "OG image uploaded", ok: !!ogImage },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <div className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 ${item.ok ? "bg-emerald-100 text-emerald-600" : "bg-slate-100 text-slate-300"}`}>
                <i className={`text-[10px] ${item.ok ? "ri-check-line" : "ri-subtract-line"}`}></i>
              </div>
              <span className={`text-xs ${item.ok ? "text-slate-600" : "text-slate-400"}`}>{item.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          {saving ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Saving...</> : saved ? <><i className="ri-check-line"></i>Saved!</> : <><i className="ri-save-line"></i>Save SEO Settings</>}
        </button>
      </div>
    </div>
  );
}